import maxBy from "lodash/maxBy";
import minBy from "lodash/minBy";
import { PotentialWarningsDateRange } from "PFApp/booking/services/api";

import { BookingDataItem, BookingFormMode } from "../booking_form.types";

// Extracts YYYY-MM-DD from date strings like "2026-04-03" or "2026-04-03T23:59:59Z"
const toDateOnly = (dateString: string): string => dateString.slice(0, 10);

const areDatesEqual = (date1: string, date2: string): boolean => toDateOnly(date1) === toDateOnly(date2);

export const hasAvailabilityFieldsChanged = (current: BookingDataItem, initial: BookingDataItem): boolean => {
  const hasDateRangeChanged =
    !areDatesEqual(current.startDate, initial.startDate) || !areDatesEqual(current.endDate, initial.endDate);
  const hasCategoryChanged = current.category?.id !== initial.category?.id;
  const hasRequirementTypeChanged = current.requirement?.type !== initial.requirement?.type;
  const hasRequirementValueChanged = current.requirement?.value !== initial.requirement?.value;
  const hasOverridesDiaryTimeChanged = current.overridesDiaryTime !== initial.overridesDiaryTime;
  const hasOverridesNonWorkingTimeChanged =
    current.overridesNonWorkingTime !== initial.overridesNonWorkingTime;

  return (
    hasDateRangeChanged ||
    hasCategoryChanged ||
    hasRequirementTypeChanged ||
    hasRequirementValueChanged ||
    hasOverridesDiaryTimeChanged ||
    hasOverridesNonWorkingTimeChanged
  );
};

const findMatchingBooking = (
  current: BookingDataItem,
  initialBookings: BookingDataItem[],
  isCloneMode: boolean
): BookingDataItem | undefined =>
  initialBookings.find(({ id, key }) => (isCloneMode ? key === current.key : id === current.id));

export const getAvailabilityAffectedBookings = (
  currentBookings: BookingDataItem[],
  initialBookings: BookingDataItem[],
  mode: BookingFormMode
): BookingDataItem[] => {
  const isCloneMode = [BookingFormMode.CloneSingle, BookingFormMode.CloneAll].includes(mode);

  return currentBookings.filter((current) => {
    const initial = findMatchingBooking(current, initialBookings, isCloneMode);

    return !initial || hasAvailabilityFieldsChanged(current, initial);
  });
};

export const calculateDateRange = (bookings: BookingDataItem[]): PotentialWarningsDateRange | undefined => {
  if (bookings.length === 0) {
    return undefined;
  }

  const minBooking = minBy(bookings, "startDate");
  const maxBooking = maxBy(bookings, "endDate");

  return {
    min: minBooking!.startDate,
    max: maxBooking!.endDate
  };
};
